import { getMatchdayNumber, getMatchdays } from './matchdays';

const matchDate = match => {
  const raw = String(match?.fecha || '');
  const local = raw.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const parts = local ? [local[3], local[2], local[1]] : iso ? [iso[1], iso[2], iso[3]] : null;
  if (!parts) return null;
  const [year, month, day] = parts.map(Number);
  const time = String(match.hora || '').match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  const [hour, minute, second] = time ? [time[1], time[2], time[3] || 0].map(Number) : [0, 0, 0];
  const date = new Date(year, month - 1, day, hour, minute, second);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
    && hour <= 23 && minute <= 59 && second <= 59 ? date.getTime() : null;
};
const matchCode = match => match?.codacta ?? match?.codActa ?? match?.idActa;
const sameMatch = (first, second) => {
  const code = matchCode(first), otherCode = matchCode(second);
  if (code != null && otherCode != null) return String(code) === String(otherCode);
  const id = first?.idPartido ?? first?.id, otherId = second?.idPartido ?? second?.id;
  return id != null && otherId != null && String(id) === String(otherId);
};

export function reportPreviousMatch(calendar, teamId, teamName, target) {
  const played = reportRecentMatches(calendar, teamId, teamName, Number.MAX_SAFE_INTEGER);
  const targetDate = matchDate(target);
  const targetRound = getMatchdayNumber(target) ?? getMatchdayNumber(getMatchdays(calendar)
    .find(round => round.partidos?.some(match => sameMatch(match, target))));
  const candidates = played.filter(match => {
    if (sameMatch(match, target)) return false;
    const date = matchDate(match);
    if (targetDate !== null) return date !== null && date < targetDate;
    return Number.isFinite(Number(targetRound)) && Number(targetRound) > 0 && match.round < Number(targetRound);
  });
  return candidates.sort((a, b) => targetDate !== null ? matchDate(a) - matchDate(b) : a.round - b.round).at(-1) ?? null;
}

export function reportMatchDismissals(acta, home) {
  const cards = acta?.[home ? 'tarjetasLocales' : 'tarjetasVisitantes'];
  const roster = acta?.[home ? 'jugadoresLocales' : 'jugadoresVisitantes'];
  if (!Array.isArray(cards)) return null;
  const players = new Map();
  for (const card of cards) {
    if (!card || card.esCuerpoTecnico === true || card.esEntrenador === true) continue;
    const secondYellow = card.esSegundaAmarilla === true;
    if (!secondYellow && String(card.tipoTarjeta ?? card.tipo) !== '101') continue;
    const id = card.codJugador ?? card.idJugador;
    const player = (Array.isArray(roster) ? roster : []).find(item => {
      const rosterId = item.codJugador ?? item.idJugador;
      return id != null && rosterId != null ? String(id) === String(rosterId)
        : card.nombreJugador && item.nombre === card.nombreJugador;
    });
    // Card feeds can also include staff. Only a recorded roster identity proves this is a player.
    if (!player) return null;
    const key = String(player.codJugador ?? player.idJugador ?? player.nombre);
    const previous = players.get(key);
    if (!previous || secondYellow) players.set(key, {
      id: player.codJugador ?? player.idJugador, name: player.nombre || card.nombreJugador,
      minute: card.minuto, reason: secondYellow ? 'doubleYellow' : 'red',
    });
  }
  return [...players.values()];
}

export const reportNumber = (value) => {
  if (!['number', 'string'].includes(typeof value) || String(value).trim() === '') return null;
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : null;
};

export const reportSum = (...values) => {
  const numbers = values.map(reportNumber);
  return numbers.some(value => value === null) ? null : numbers.reduce((sum, value) => sum + value, 0);
};

export const reportRate = (value, played, multiplier = 1) => (
  Number.isFinite(value) && Number.isFinite(played) && played > 0 ? value / played * multiplier : null
);

export function reportDistribution(values, total) {
  const expected = reportNumber(total);
  const counts = values.map(reportNumber);
  const reconciled = expected !== null && counts.reduce((sum, value) => sum + (value ?? 0), 0) === expected
    && values.every((value, index) => value === undefined || counts[index] !== null);
  const resolved = counts.map((value, index) => value === null && values[index] === undefined && reconciled ? 0 : value);
  return {
    counts: resolved,
    shares: expected > 0 && reconciled ? resolved.map(value => value / expected) : null,
  };
}

export function reportTeamMetrics(statistics, standing) {
  const played = reportNumber(standing?.jugados) ?? reportNumber(statistics?.partidos?.partidosJugados);
  const wins = reportNumber(standing?.ganados);
  const points = reportNumber(standing?.puntos);
  const goalsFor = reportNumber(standing?.golesFavor);
  const goalsAgainst = reportNumber(standing?.golesContra);
  return {
    played, wins, points, goalsFor, goalsAgainst,
    position: reportNumber(standing?.posicion),
    pointsRate: reportRate(points, played),
    winRate: reportRate(wins, played, 100),
    goalsForRate: reportRate(goalsFor, reportNumber(standing?.jugados)),
    goalsAgainstRate: reportRate(goalsAgainst, reportNumber(standing?.jugados)),
  };
}

export function reportRecentMatches(calendar, teamId, teamName, limit = 10) {
  const matches = new Map();
  const conflicts = new Set();
  const id = String(teamId ?? '');
  getMatchdays(calendar).forEach(round => (round.partidos || []).forEach(match => {
    const homeId = match.codEquipoLocal ?? match.idEquipoLocal;
    const awayId = match.codEquipoVisitante ?? match.idEquipoVisitante;
    const hasIds = homeId != null && awayId != null && id !== '';
    const home = hasIds ? String(homeId) === id : match.equipoLocal === teamName;
    const away = hasIds ? String(awayId) === id : match.equipoVisitante === teamName;
    if (!home && !away) return;
    const key = String(match.idPartido ?? match.id ?? match.codActa
      ?? `${getMatchdayNumber(round)}:${homeId ?? match.equipoLocal}:${awayId ?? match.equipoVisitante}`);
    const entry = { ...match, round: getMatchdayNumber(round), home };
    const previous = matches.get(key);
    if (previous && ['golesCasa', 'golesVisitante', 'partidoJugado', 'home'].some(field =>
      String(previous[field]) !== String(entry[field]))) conflicts.add(key);
    matches.set(key, entry);
  }));
  const date = value => {
    const raw = String(value || '');
    const local = raw.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
    return Date.parse(local ? `${local[3]}-${local[2]}-${local[1]}` : raw);
  };
  const played = [...matches].filter(([key, match]) => !conflicts.has(key)
    && match.partidoJugado === true
    && [match.golesCasa, match.golesVisitante].every(value => Number.isInteger(reportNumber(value))))
    .map(([, match]) => {
      const forGoals = Number(match.home ? match.golesCasa : match.golesVisitante);
      const againstGoals = Number(match.home ? match.golesVisitante : match.golesCasa);
      return {
        ...match, forGoals, againstGoals,
        opponent: match.home ? match.equipoVisitante : match.equipoLocal,
        points: forGoals > againstGoals ? 3 : forGoals === againstGoals ? 1 : 0,
        outcome: forGoals > againstGoals ? 'win' : forGoals === againstGoals ? 'draw' : 'loss',
      };
    });
  const dated = played.every(match => Number.isFinite(date(match.fecha)));
  return played.sort((a, b) => (dated ? date(a.fecha) - date(b.fecha) : 0)
    || (a.round ?? 0) - (b.round ?? 0)).slice(-limit);
}

export function reportGoalIntervals(statistics, standing) {
  const intervals = ['0_15', '15_30', '30_45', '45_60', '60_75', '75_90'];
  const goals = statistics?.goles || {};
  const sameSample = reportNumber(statistics?.partidos?.partidosJugados) !== null
    && reportNumber(statistics?.partidos?.partidosJugados) === reportNumber(standing?.jugados);
  const buildSeries = (prefixes, totalKeys, standingTotal) => {
    const officialTotal = sameSample ? reportNumber(standingTotal) : null;
    const aggregateTotal = reportSum(...totalKeys.map(key => goals[key]));
    const expected = officialTotal ?? aggregateTotal;
    const keys = intervals.flatMap(interval => ['Local', 'Visitante'].flatMap(side => prefixes.map(prefix => `${prefix}${side}${interval}`)));
    const invalid = keys.some(key => goals[key] !== undefined && reportNumber(goals[key]) === null);
    const knownSum = keys.reduce((sum, key) => sum + (reportNumber(goals[key]) ?? 0), 0);
    // Sparse counters can only be completed when an independent total accounts for every goal.
    const reconciled = !invalid && expected !== null && knownSum === expected;
    const contradictory = (officialTotal !== null && aggregateTotal !== null && officialTotal !== aggregateTotal)
      || (expected !== null && (knownSum > expected
        || (keys.every(key => reportNumber(goals[key]) !== null) && knownSum !== expected)));
    return (interval, sides = ['Local', 'Visitante']) => {
      if (contradictory) return null;
      return reportSum(...sides.flatMap(side => prefixes.map(prefix => {
        const value = goals[`${prefix}${side}${interval}`];
        return value === undefined && reconciled ? 0 : value;
      })));
    };
  };
  const forGoals = buildSeries(['goles', 'golesPenalti', 'golesPropia'],
    ['golesTotales', 'golesTotalesPenalti', 'golesTotalesPropia'], standing?.golesFavor);
  const againstGoals = buildSeries(['golesRecibidos', 'golesRecibidosPenalti', 'golesRecibidosPropia'],
    ['golesTotalesRecibidos', 'golesTotalesRecibidosPenalti', 'golesTotalesRecibidosPropia'], standing?.golesContra);
  return intervals.map(interval => ({
    interval, label: `${interval.replace('_', '-')}′`,
    forGoals: forGoals(interval), againstGoals: againstGoals(interval),
    homeFor: forGoals(interval, ['Local']), awayFor: forGoals(interval, ['Visitante']),
    homeAgainst: againstGoals(interval, ['Local']), awayAgainst: againstGoals(interval, ['Visitante']),
  }));
}

export function reportTrend(matches, standing) {
  const recent = matches.slice(-5);
  const played = recent.length;
  const scored = recent.reduce((total, match) => total + Number(match.home ? match.golesCasa : match.golesVisitante), 0);
  const conceded = recent.reduce((total, match) => total + Number(match.home ? match.golesVisitante : match.golesCasa), 0);
  const points = recent.reduce((total, match) => {
    const own = Number(match.home ? match.golesCasa : match.golesVisitante);
    const other = Number(match.home ? match.golesVisitante : match.golesCasa);
    return total + (own > other ? 3 : own === other ? 1 : 0);
  }, 0);
  const summary = (played, points, goalsFor, goalsAgainst) => ({
    played, points, goalsFor, goalsAgainst, pointsRate: reportRate(points, played),
    goalsForRate: reportRate(goalsFor, played), goalsAgainstRate: reportRate(goalsAgainst, played),
  });
  return {
    recent: summary(played, played ? points : null, played ? scored : null, played ? conceded : null),
    cumulative: summary(reportNumber(standing?.jugados), reportNumber(standing?.puntos),
      reportNumber(standing?.golesFavor), reportNumber(standing?.golesContra)),
  };
}

export function reportScorerConcentration(players = [], statistics, standing) {
  const total = reportNumber(standing?.golesFavor);
  const fields = ['golesTotales', 'golesTotalesPenalti'];
  const raw = player => fields.map(field => Object.hasOwn(player.goles || {}, field) ? player.goles[field] : player[field]);
  const available = players.some(player => raw(player).some(value => reportNumber(value) !== null));
  const invalid = players.some(player => raw(player).some(value =>
    value !== undefined && (!Number.isInteger(reportNumber(value)) || reportNumber(value) === null)));
  const identifiers = players.map(player => player.idJugador ?? player.id).filter(value => value != null).map(String);
  const unique = new Set(identifiers).size === identifiers.length;
  const rows = players.map(player => ({ name: player.nombre,
    goals: reportSum(...raw(player).map(value => value === undefined ? 0 : value)) }));
  const recorded = reportSum(...rows.map(row => row.goals));
  const ownRaw = statistics?.goles?.golesTotalesPropia;
  const own = reportNumber(ownRaw);
  const ownValid = ownRaw === undefined || (own !== null && Number.isInteger(own));
  const aggregatesValid = fields.every((field, index) => statistics?.goles?.[field] === undefined
    || reportNumber(statistics.goles[field]) === players.reduce((total, player) => total + (reportNumber(raw(player)[index]) ?? 0), 0));
  const covered = available && !invalid && unique && ownValid && aggregatesValid && Number.isInteger(total)
    && reportNumber(standing?.jugados) > 0 && reportNumber(standing?.jugados) === reportNumber(statistics?.partidos?.partidosJugados)
    && recorded !== null && recorded + (own ?? 0) === total;
  const leaders = rows.filter(row => row.goals > 0).sort((a, b) => b.goals - a.goals).slice(0, 3);
  const topGoals = leaders.length || covered ? leaders.reduce((total, row) => total + row.goals, 0) : null;
  return { leaders, total, topGoals, covered, others: covered ? total - topGoals : null,
    share: covered && total > 0 ? topGoals / total * 100 : null };
}
